import { type ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import BrandWordmark from "./BrandWordmark";
import IpadGuide, { IpadGuideSpotlight } from "./IpadGuide";
import { Trans, useTranslation } from "./i18n";
import LandingRecordDemo from "./LandingRecordDemo";
import LanguageSwitcher from "./LanguageSwitcher";
import { DRIVE_SYNC_RELEASED } from "./lib/features";
import { useWorkspaceScreen } from "./lib/useWorkspaceScreen";
import SyncGuide, { SyncSpotlight } from "./SyncGuide";
import ToolIcon from "./ToolIcon";
import "./marketing-site.css";

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const element = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (
      !element.current ||
      !window.IntersectionObserver ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add("site-entered");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  // Content stays visible even if animation or observation is unavailable.
  return (
    <div className={`site-reveal ${className}`} ref={element}>
      {children}
    </div>
  );
}

function EntryActions({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation("landing");
  return (
    <div className={`site-actions${compact ? " site-actions-compact" : ""}`}>
      <a className="site-button site-button-primary" href="/demo">
        {compact ? t("demoNav") : t("demoAction")} <Arrow />
      </a>
      <a className="site-button site-button-secondary" href="/app">
        {t("trialNav")} <span aria-hidden="true">→</span>
      </a>
    </div>
  );
}

function ProductImage({
  name,
  alt,
  hero = false,
  width = 1265,
  height = 712,
}: {
  name: string;
  alt: string;
  hero?: boolean;
  width?: number;
  height?: number;
}) {
  const { t } = useTranslation("marketing");
  return (
    <figure className={`site-product-image${hero ? " site-product-hero" : ""}`}>
      <div className="site-window-bar" aria-hidden="true">
        <span />
        <span />
        <span />
        <b>{t("heroScreenLabel")}</b>
      </div>
      <img src={`/marketing/${name}`} width={width} height={height} alt={alt} loading={hero ? "eager" : "lazy"} />
      <figcaption>{t("heroCaption")}</figcaption>
    </figure>
  );
}

const teachingTools = [
  {
    id: "classroom",
    label: "captureTab",
    title: "captureTitle",
    description: "captureDescription",
    points: ["capturePoint1", "capturePoint2", "capturePoint3"],
    image: "site-records.png",
    alt: "captureAlt",
  },
  {
    id: "coursework",
    label: "gradesTab",
    title: "gradesTitle",
    description: "gradesDescription",
    points: ["gradesPoint1", "gradesPoint2", "gradesPoint3"],
    image: "site-coursework-overview.png",
    alt: "gradesAlt",
  },
  {
    id: "reports",
    label: "reportsTab",
    title: "reportsTitle",
    description: "reportsDescription",
    points: ["reportsPoint1", "reportsPoint2", "reportsPoint3"],
    image: "site-grade-export.png",
    alt: "reportsAlt",
  },
  {
    id: "terms",
    label: "termsTab",
    title: "termsTitle",
    description: "termsDescription",
    points: ["termsPoint1", "termsPoint2", "termsPoint3"],
    image: "site-term-overview.png",
    alt: "termsAlt",
  },
  {
    id: "groups",
    label: "groupsTab",
    title: "groupsTitle",
    description: "groupsDescription",
    points: ["groupsPoint1", "groupsPoint2", "groupsPoint3"],
    image: "site-groups.png",
    alt: "groupsAlt",
  },
] as const;

const homeroomTools = [
  {
    id: "seating",
    label: "seatingTab",
    title: "seatingTitle",
    description: "seatingDescription",
    points: ["seatingPoint1", "seatingPoint2", "seatingPoint3"],
    image: "site-seating.jpg",
    alt: "seatingAlt",
  },
  {
    id: "attendance",
    label: "attendanceTab",
    title: "attendanceTitle",
    description: "attendanceDescription",
    points: ["attendancePoint1", "attendancePoint2", "attendancePoint3"],
    image: "site-attendance.jpg",
    alt: "attendanceAlt",
  },
  {
    id: "missing-homework",
    label: "missingTab",
    title: "missingTitle",
    description: "missingDescription",
    points: ["missingPoint1", "missingPoint2", "missingPoint3"],
    image: "site-homeroom-homework.png",
    alt: "missingAlt",
  },
  {
    id: "homeroom",
    label: "homeroomTab",
    title: "homeroomTitle",
    description: "homeroomDescription",
    points: ["homeroomPoint1", "homeroomPoint2", "homeroomPoint3"],
    image: "site-homeroom.jpg",
    alt: "homeroomAlt",
  },
  {
    id: "duties",
    label: "dutiesTab",
    title: "dutiesTitle",
    description: "dutiesDescription",
    points: ["dutiesPoint1", "dutiesPoint2", "dutiesPoint3"],
    image: "site-duties.png",
    alt: "dutiesAlt",
  },
  {
    id: "students",
    label: "studentsTab",
    title: "studentsTitle",
    description: "studentsDescription",
    points: ["studentsPoint1", "studentsPoint2", "studentsPoint3"],
    image: "site-homeroom-records.png",
    alt: "studentsAlt",
  },
] as const;

const showcaseGroups = [
  {
    id: "teaching",
    title: "teachingToolsTitle",
    description: "teachingToolsDescription",
    tools: teachingTools,
  },
  {
    id: "homeroom",
    title: "homeroomToolsTitle",
    description: "homeroomToolsDescription",
    tools: homeroomTools,
  },
] as const;

function ToolShowcase({ group }: { group: (typeof showcaseGroups)[number] }) {
  const { t } = useTranslation("marketing");
  const [selected, setSelected] = useState(0);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  return (
    <Reveal className="site-showcase">
      <section aria-labelledby={`showcase-${group.id}-title`}>
        <div className="site-showcase-bar">
          <div className="site-showcase-heading">
            <h2 id={`showcase-${group.id}-title`}>{t(group.title)}</h2>
            <p>{t(group.description)}</p>
          </div>
          <div className="site-tool-tabs" role="tablist" aria-labelledby={`showcase-${group.id}-title`}>
            {group.tools.map((tool, index) => (
              <button
                key={tool.id}
                type="button"
                role="tab"
                id={`showcase-${group.id}-${tool.id}-tab`}
                aria-controls={`showcase-${group.id}-${tool.id}-panel`}
                aria-selected={selected === index}
                tabIndex={selected === index ? 0 : -1}
                ref={(button) => {
                  buttons.current[index] = button;
                }}
                onClick={() => setSelected(index)}
                onKeyDown={(event) => {
                  if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                  event.preventDefault();
                  const next =
                    event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? group.tools.length - 1
                        : (index + (event.key === "ArrowRight" ? 1 : -1) + group.tools.length) % group.tools.length;
                  setSelected(next);
                  buttons.current[next]?.focus({ preventScroll: true });
                }}
              >
                {t(tool.label)}
              </button>
            ))}
          </div>
        </div>
        {group.tools.map((tool, index) => (
          <div
            className="site-showcase-panel"
            role="tabpanel"
            key={tool.id}
            id={`showcase-${group.id}-${tool.id}-panel`}
            aria-labelledby={`showcase-${group.id}-${tool.id}-tab`}
            hidden={selected !== index}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: Static tab panels need a keyboard stop after their selected tab.
            tabIndex={0}
          >
            <ProductImage name={tool.image} alt={t(tool.alt)} />
            <div className="site-showcase-copy">
              <p className="site-eyebrow">{t(tool.label)}</p>
              <h3>
                <Trans t={t} i18nKey={tool.title} />
              </h3>
              <p>{t(tool.description)}</p>
              <ul className="site-check-list">
                {tool.points.map((point) => (
                  <li key={point}>{t(point)}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>
    </Reveal>
  );
}

function Features() {
  return (
    <div className="site-section site-features" id="features">
      {showcaseGroups.map((group) => (
        <ToolShowcase key={group.id} group={group} />
      ))}
    </div>
  );
}

function Tools() {
  const { t } = useTranslation("marketing");
  const tools = [
    ["seating", "toolSeating", "toolSeatingDescription"],
    ["calendar", "toolCalendar", "toolCalendarDescription"],
    ["import", "toolImport", "toolImportDescription"],
    ["export", "toolExport", "toolExportDescription"],
  ] as const;
  return (
    <section className="site-tools" aria-labelledby="tools-title">
      <div className="site-section">
        <Reveal className="site-section-heading">
          <p className="site-eyebrow">{t("toolsKicker")}</p>
          <h2 id="tools-title">{t("toolsTitle")}</h2>
        </Reveal>
        <div className="site-tools-grid">
          {tools.map(([icon, title, description]) => (
            <Reveal key={title}>
              <article className="site-tool">
                <span className="site-tool-symbol" aria-hidden="true">
                  <ToolIcon name={icon} />
                </span>
                <h3>{t(title)}</h3>
                <p>{t(description)}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const faqItems = [
  ["faqDemoQuestion", "faqDemoAnswer"],
  ["faqDeviceQuestion", "faqDeviceAnswer"],
  ["faqStorageQuestion", DRIVE_SYNC_RELEASED ? "faqStorageAnswerSynced" : "faqStorageAnswer"],
  ["faqSyncQuestion", DRIVE_SYNC_RELEASED ? "faqSyncAnswerAvailable" : "faqSyncAnswer"],
  ["faqBackupQuestion", "faqBackupAnswer"],
  ["faqPriceQuestion", "faqPriceAnswer"],
  ["faqInternetQuestion", "faqInternetAnswer"],
  ["faqImportQuestion", "faqImportAnswer"],
  ["faqRealQuestion", "faqRealAnswer"],
  ["faqQuizQuestion", DRIVE_SYNC_RELEASED ? "faqQuizAnswerSynced" : "faqQuizAnswer"],
] as const;

function Faq({ full = false }: { full?: boolean }) {
  const { t } = useTranslation("marketing");
  return (
    <section className="site-section site-faq" id="faq" aria-labelledby="faq-title">
      <div className="site-faq-heading">
        <p className="site-eyebrow">{t("faqKicker")}</p>
        <h2 id="faq-title">{full ? t("faqAllTitle") : t("faqTitle")}</h2>
        {!full && (
          <a className="site-text-link" href="/guide">
            {t("faqMore")} <Arrow />
          </a>
        )}
      </div>
      <div className="site-faq-list">
        {(full ? faqItems : faqItems.slice(0, 6)).map(([question, answer]) => (
          <details key={question}>
            <summary>
              {t(question)}
              <span aria-hidden="true">+</span>
            </summary>
            <p>{t(answer)}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ClosingInvitation() {
  const { t } = useTranslation("marketing");
  const { t: landing } = useTranslation("landing");
  return (
    <section className="site-invitation" aria-labelledby="invitation-title">
      <div className="site-section">
        <div>
          <p className="site-eyebrow">{t("ctaKicker")}</p>
          <h2 id="invitation-title">{t("ctaTitle")}</h2>
          <p>{t("ctaDescription")}</p>
        </div>
        <EntryActions />
        <small>{landing("entryNote")}</small>
      </div>
    </section>
  );
}

function Home() {
  const { t } = useTranslation("marketing");
  const { t: landing } = useTranslation("landing");
  const classroomImage = "site-records-palette.jpg";
  const interactive = useWorkspaceScreen();
  return (
    <>
      <section className="site-hero" aria-labelledby="landing-title">
        <div className="site-hero-inner">
          <div className="site-hero-copy">
            <p className="site-eyebrow">{t("heroKicker")}</p>
            <h1 id="landing-title" tabIndex={-1}>
              <Trans t={t} i18nKey="heroTitle" />
            </h1>
            <p className="site-lead">{t("heroDescription")}</p>
            <EntryActions />
            <p className="site-entry-note">{landing("entryNote")}</p>
            <a className="site-text-link site-hero-sync-link" href="/sync">
              {t("syncHeroLink")} <Arrow />
            </a>
            <a className="site-hero-jump" href="#features">
              {t("heroLink")} <span aria-hidden="true">↓</span>
            </a>
          </div>
          <div className="site-hero-visual">
            <ProductImage name={classroomImage} alt={t("heroAlt")} hero />
          </div>
        </div>
        <div className="site-value-line">
          {(["pointLanguage", "pointRoles", "pointDevices", "pointStorage"] as const).map((key) => (
            <span key={key}>{t(DRIVE_SYNC_RELEASED && key === "pointDevices" ? "pointSyncAvailable" : key)}</span>
          ))}
        </div>
      </section>
      <section className="site-section site-mission" aria-labelledby="mission-title">
        <Reveal>
          <p className="site-eyebrow">{t("missionKicker")}</p>
          <h2 id="mission-title">
            <Trans t={t} i18nKey="missionTitle" />
          </h2>
          <p>{t("missionDescription")}</p>
          <p className="site-mission-benefit">{t("missionBenefit")}</p>
        </Reveal>
        <Reveal className="site-role-list">
          <article>
            <span aria-hidden="true">01</span>
            <div>
              <h3>{t("missionTeaching")}</h3>
              <p>{t("missionTeachingDescription")}</p>
            </div>
          </article>
          <article>
            <span aria-hidden="true">02</span>
            <div>
              <h3>{t("missionHomeroom")}</h3>
              <p>{t("missionHomeroomDescription")}</p>
            </div>
          </article>
        </Reveal>
      </section>
      <SyncSpotlight />
      <IpadGuideSpotlight />
      <Features />
      <Tools />
      <section className="site-section site-interaction" aria-labelledby="interaction-title">
        <div>
          <p className="site-eyebrow">{t("interactionKicker")}</p>
          <h2 id="interaction-title">
            <Trans t={t} i18nKey={interactive ? "interactionTitle" : "interactionMobileTitle"} />
          </h2>
          <p>{interactive ? t("interactionDescription") : t("interactionMobile")}</p>
        </div>
        {interactive ? (
          <div className="site-interaction-device">
            <LandingRecordDemo />
          </div>
        ) : (
          <ProductImage name={classroomImage} alt={t("heroAlt")} />
        )}
      </section>
      <section className="site-data-teaser" aria-labelledby="data-teaser-title">
        <div className="site-section">
          <div className="site-data-mark" aria-hidden="true">
            ↙<span>{t("localLabel")}</span>
          </div>
          <div>
            <p className="site-eyebrow">{t("dataTeaserKicker")}</p>
            <h2 id="data-teaser-title">
              <Trans t={t} i18nKey={DRIVE_SYNC_RELEASED ? "dataTeaserTitleSynced" : "dataTeaserTitle"} />
            </h2>
            <p>{t(DRIVE_SYNC_RELEASED ? "dataTeaserDescriptionSynced" : "dataTeaserDescription")}</p>
            <p className="site-data-detail">{t(DRIVE_SYNC_RELEASED ? "dataTeaserDetailSynced" : "dataTeaserDetail")}</p>
            <a className="site-text-link" href="/data">
              {t("dataLink")} <Arrow />
            </a>
          </div>
        </div>
      </section>
      <Faq />
      <ClosingInvitation />
    </>
  );
}

function DataPage() {
  const { t } = useTranslation("marketing");
  const { t: landing } = useTranslation("landing");
  return (
    <>
      <section className="site-page-hero site-section">
        <p className="site-eyebrow">{t("dataPageKicker")}</p>
        <h1 id="landing-title" tabIndex={-1}>
          <Trans t={t} i18nKey="dataPageTitleText" />
        </h1>
        <p className="site-lead">{t(DRIVE_SYNC_RELEASED ? "dataPageLeadSynced" : "dataPageLead")}</p>
      </section>
      <section className="site-section site-data-explainer" aria-label={t("dataFlowCaption")}>
        <div className="site-storage-diagram">
          <div className="site-storage-device">
            <span className="site-eyebrow">{t("localLabel")}</span>
            <h2>{t("dataFlowDevice")}</h2>
            <p>{t("dataFlowRecords")}</p>
          </div>
          <span className="site-storage-arrow" aria-hidden="true">
            ↓
          </span>
          <div className="site-storage-backup">{t("dataFlowBackup")}</div>
          {DRIVE_SYNC_RELEASED && (
            <a className="site-storage-backup" href="/sync">
              {t("syncNav")} · Google Drive <Arrow />
            </a>
          )}
          <div className="site-storage-account">
            <span className="site-eyebrow">{t("accountLabel")}</span>
            <h3>{t("dataFlowAccount")}</h3>
            <p>{t("dataFlowAccountDetail")}</p>
          </div>
        </div>
        <div className="site-data-explainer-copy">
          <p className="site-eyebrow">{landing("dataKicker")}</p>
          <h2>{landing(DRIVE_SYNC_RELEASED ? "dataTitleSynced" : "dataTitle")}</h2>
          <p>{landing(DRIVE_SYNC_RELEASED ? "dataDescriptionSynced" : "dataDescription")}</p>
          <h3>{t("dataAccountTitle")}</h3>
          <p>{t("dataAccountDescription")}</p>
          <a className="site-text-link" href="/privacy">
            {t("policyLink")} <Arrow />
          </a>
        </div>
      </section>
      <section className="site-section site-data-facts">
        {(
          [
            ["localTitle", DRIVE_SYNC_RELEASED ? "localDescriptionSynced" : "localDescription"],
            ["backupTitle", "backupDescription"],
            [
              DRIVE_SYNC_RELEASED ? "syncAvailableTitle" : "syncTitle",
              DRIVE_SYNC_RELEASED ? "syncAvailableDescription" : "syncDescription",
            ],
          ] as const
        ).map(([title, description], index) => (
          <article key={title}>
            <span className="site-eyebrow">0{index + 1}</span>
            <h2>{landing(title)}</h2>
            <p>{landing(description)}</p>
          </article>
        ))}
      </section>
      {DRIVE_SYNC_RELEASED && (
        <div className="site-section">
          <a className="site-text-link" href="/sync">
            {t("syncGuideLink")} <Arrow />
          </a>
        </div>
      )}
      <section className="site-section site-backup-checklist">
        <h2>{t("dataChecklistTitle")}</h2>
        <ol>
          {(["dataChecklist1", "dataChecklist2", "dataChecklist3"] as const).map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ol>
      </section>
      <section className="site-section site-read-only">
        <div>
          <p className="site-eyebrow">{landing("betaDetails")}</p>
          <h2>{t("dataReadOnlyTitle")}</h2>
          <p>{t("dataReadOnlyDescription")}</p>
        </div>
        <p>{landing("betaAccessNotice")}</p>
      </section>
      <ClosingInvitation />
    </>
  );
}

function SyncPage() {
  const { t } = useTranslation("marketing");
  return (
    <>
      <section className="site-page-hero site-section">
        <p className="site-eyebrow site-sync-status">{t(DRIVE_SYNC_RELEASED ? "syncStatusAvailable" : "syncStatus")}</p>
        <h1 id="landing-title" tabIndex={-1}>
          {t("syncNav")}
        </h1>
        <p className="site-lead">{t(DRIVE_SYNC_RELEASED ? "syncLeadAvailable" : "syncLead")}</p>
        <aside className="site-sync-browser-note" aria-labelledby="sync-browser-title">
          <h2 id="sync-browser-title">{t("syncBrowserTitle")}</h2>
          <p>{t("syncBrowserDescription")}</p>
          <a className="site-text-link" href="/ipad">
            {t("syncIpadGuideLink")} <Arrow />
          </a>
        </aside>
      </section>
      <SyncGuide />
      {!DRIVE_SYNC_RELEASED && (
        <section className="site-section site-sync-explainer">
          <article>
            <h2>{t("syncCurrentTitle")}</h2>
            <p>{t("syncCurrentDescription")}</p>
          </article>
          <article>
            <h2>{t("syncPlanTitle")}</h2>
            <p>{t("syncPlanDescription")}</p>
          </article>
        </section>
      )}
    </>
  );
}

function GuidePage() {
  const { t } = useTranslation("marketing");
  const { t: landing } = useTranslation("landing");
  const { t: auth } = useTranslation("auth");
  const localPreview = ["localhost", "127.0.0.1", "::1"].includes(location.hostname);
  return (
    <>
      <section className="site-page-hero site-section">
        <p className="site-eyebrow">{t("guideKicker")}</p>
        <h1 id="landing-title" tabIndex={-1}>
          <Trans t={t} i18nKey="guideTitle" />
        </h1>
        <p className="site-lead">{t("guideLead")}</p>
      </section>
      <section className="site-section site-trial-options" aria-label={landing("trialTitle")}>
        <article>
          <p className="site-eyebrow">{t("demoChoiceLabel")}</p>
          <h2>{landing("demoTitle")}</h2>
          <p>{landing("demoDescription")}</p>
          <ul className="site-check-list">
            {(["demoFeature1", "demoFeature2", "demoFeature3", "demoFeature4"] as const).map((key) => (
              <li key={key}>{landing(key)}</li>
            ))}
          </ul>
          <a className="site-button site-button-secondary" href="/demo">
            {landing("demoEntry")} <Arrow />
          </a>
        </article>
        <article className="site-trial-featured">
          <p className="site-eyebrow">{landing("betaIndex")}</p>
          <h2>{landing("betaTitle")}</h2>
          <p>{landing(DRIVE_SYNC_RELEASED ? "betaDescriptionSynced" : "betaDescription")}</p>
          <ul className="site-check-list">
            <li>{landing("betaFeature1")}</li>
            <li>{auth("benefits.account")}</li>
            <li>{landing(DRIVE_SYNC_RELEASED ? "betaFeature3Synced" : "betaFeature3")}</li>
            <li>{landing("betaFeature4")}</li>
          </ul>
          <a className="site-button site-button-primary" href="/app">
            {landing("betaEntry")} <Arrow />
          </a>
          {localPreview && (
            <a className="site-local-preview" href="/local?setup=1">
              {landing("trialPreview")} <Arrow />
            </a>
          )}
        </article>
      </section>
      <section className="site-section site-setup" aria-labelledby="setup-title">
        <h2 id="setup-title">{t("guideStepsTitle")}</h2>
        <ol>
          {(
            [
              ["guideStep1Title", "guideStep1Description"],
              ["guideStep2Title", "guideStep2Description"],
              ["guideStep3Title", "guideStep3Description"],
            ] as const
          ).map(([title, description], index) => (
            <li key={title}>
              <span aria-hidden="true">0{index + 1}</span>
              <h3>{t(title)}</h3>
              <p>{t(description)}</p>
            </li>
          ))}
        </ol>
      </section>
      <Faq full />
      <p className="site-section site-availability">{landing("availability")}</p>
      <ClosingInvitation />
    </>
  );
}

export default function MarketingSite() {
  const { pathname } = useLocation();
  const { t } = useTranslation("marketing");
  const { t: landing } = useTranslation("landing");
  const { t: brand } = useTranslation("brand");
  const page =
    pathname === "/data"
      ? "data"
      : pathname === "/guide"
        ? "guide"
        : pathname === "/sync"
          ? "sync"
          : pathname === "/ipad"
            ? "ipad"
            : "home";
  useEffect(() => {
    document.title =
      page === "data"
        ? t("dataPageTitle")
        : page === "guide"
          ? t("guidePageTitle")
          : page === "sync"
            ? t("syncPageTitle")
            : page === "ipad"
              ? t("ipadPageTitle")
              : t("homePageTitle");
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        page === "data"
          ? t(DRIVE_SYNC_RELEASED ? "dataPageDescriptionSynced" : "dataPageDescription")
          : page === "guide"
            ? t("guidePageDescription")
            : page === "sync"
              ? t(DRIVE_SYNC_RELEASED ? "syncPageDescriptionAvailable" : "syncPageDescription")
              : page === "ipad"
                ? t("ipadPageDescription")
                : t("heroDescription"),
      );
  }, [page, t]);
  return (
    <div className="marketing-site">
      <a className="skip-link" href="#landing-title">
        {landing("skip")}
      </a>
      <header className="site-header">
        <div className="site-header-inner">
          <a className="site-brand" href="/" aria-label={`${brand("name")} ${t("homeNav")}`}>
            <BrandWordmark />
          </a>
          <nav className="site-nav" aria-label={t("siteNavigation")}>
            <a href="/ipad" aria-current={page === "ipad" ? "page" : undefined}>
              {t("ipadNav")}
            </a>
            <a href="/sync" aria-current={page === "sync" ? "page" : undefined}>
              {t("syncNav")}
            </a>
            <a href="/data" aria-current={page === "data" ? "page" : undefined}>
              {t("dataNav")}
            </a>
            <a href="/guide" aria-current={page === "guide" ? "page" : undefined}>
              {t("guideNav")}
            </a>
          </nav>
          <nav className="site-header-actions" aria-label={t("entryNavigation")}>
            <LanguageSwitcher />
            <a className="site-button site-button-primary" href="/app">
              {landing("trialNav")} <Arrow />
            </a>
          </nav>
        </div>
      </header>
      <main id="main-content" tabIndex={-1}>
        {page === "data" ? (
          <DataPage />
        ) : page === "guide" ? (
          <GuidePage />
        ) : page === "sync" ? (
          <SyncPage />
        ) : page === "ipad" ? (
          <IpadGuide />
        ) : (
          <Home />
        )}
      </main>
      <footer className="site-footer site-section">
        <div className="site-footer-brand">
          <BrandWordmark />
          <p>{t("footerDescription")}</p>
          <small>{brand("transition")}</small>
        </div>
        <nav aria-label={t("footerNavigation")}>
          <a href="/">{t("homeNav")}</a>
          <a href="/ipad">{t("ipadNav")}</a>
          <a href="/data">{t("dataNav")}</a>
          <a href="/guide">{t("guideNav")}</a>
          <a href="/sync">{t("syncNav")}</a>
        </nav>
        <nav aria-label={landing("legalNavigation")}>
          <a href="/privacy">{landing("privacyLink")}</a>
          <a href="/terms">{landing("termsLink")}</a>
          <a href="/data-processing">{landing("processingLink")}</a>
          <a href="/support">{landing("supportLink")}</a>
        </nav>
        <p className="site-footer-note">{t("footerDeviceNote")}</p>
      </footer>
    </div>
  );
}
